import { apiFetch } from "../../../shared/utils/http";
import { logger } from "../../../shared/utils/logger";

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const withJitter = (base: number) => base + Math.floor(Math.random() * 50);

export async function finalizeSessionOnClient() {
  logger.info("[sessionFinalize] Starting client-side session finalization...");

  const maxStatusAttempts = 3;
  let status: any = null;

  for (let attempt = 1; attempt <= maxStatusAttempts; attempt++) {
    try {
      logger.debug(`[sessionFinalize] Checking session status (attempt ${attempt}/${maxStatusAttempts})`);
      status = await apiFetch("/session/status", { credentials: "include" }).then(r => r.json());

      logger.debug("[sessionFinalize] /session/status response:", status);

      if (status?.authenticated) {
        logger.info("[sessionFinalize] Session is authenticated on attempt", attempt);
        break;
      }
    } catch (err) {
      logger.warn(`[sessionFinalize] Failed to fetch /session/status (attempt ${attempt}):`, err);
    }

    if (attempt < maxStatusAttempts) {
      const delay = withJitter(100 * attempt);
      logger.debug(`[sessionFinalize] Retrying session status after ${delay}ms...`);
      await sleep(delay);
    }
  }

  if (!status?.authenticated) {
    logger.error("[sessionFinalize] Session not established after retries.");
    throw new Error("Session not established");
  }

  const profile = status.user;
  logger.info("[sessionFinalize] User profile retrieved:", profile);

  const getCsrf = async () => {
    logger.debug("[sessionFinalize] Fetching CSRF token...");
    const r = await fetch("/api/csrf-token", { method: "GET", credentials: "include" });
    if (!r.ok) {
      logger.error(`[sessionFinalize] Failed to fetch CSRF token: HTTP ${r.status}`);
      throw new Error(`Failed to fetch CSRF token: ${r.status}`);
    }
    const { csrfToken } = await r.json();
    localStorage.setItem("csrfToken", csrfToken);
    logger.info("[sessionFinalize] CSRF token stored in localStorage");
  };

  const maxCsrfAttempts = 2;
  for (let attempt = 1; attempt <= maxCsrfAttempts; attempt++) {
    try {
      await getCsrf();
      break;
    } catch (e) {
      logger.warn(`[sessionFinalize] CSRF fetch failed (attempt ${attempt}):`, e);
      if (attempt === maxCsrfAttempts) throw e;
      await sleep(withJitter(150));
    }
  }

  return profile as {
    email?: string;
    firstName?: string;
    lastName?: string;
    ppsn?: string;
    birthDate?: string;
  };
}
